import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addDoc, Timestamp, collection } from "firebase/firestore";
import { db } from "../firebase/config";
import { motion } from "framer-motion";
import { LEAD_TYPES, submitLeadEmail } from "../services/leadEmail";
import { DEALERSHIP_OPTIONS, getDealershipByEmail } from "../data/dealerships";
import { pumaCampaignSchema } from "../lib/leadFormSchemas";
import { Button } from "../components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function Puma2024() {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(pumaCampaignSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      dealership: "",
    },
  });

  const marketingCampaign = async (data, slug) => {
    try {
      const selectedDealership = getDealershipByEmail(data.dealership);

      await addDoc(collection(db, slug), {
        name: data.name ?? "",
        email: data.email ?? "",
        phone: data.phone ?? "",
        dealership: data.dealership ?? "",
        text: "",
        date: Timestamp.fromDate(new Date()),
        model: "Puma",
      });

      if (window.gtag) {
        window.gtag("event", "conversion", {
          send_to: "AW-17814485194/HOcACLil1d0bEMrxza5C",
        });
      }

      await submitLeadEmail({
        type: LEAD_TYPES.CAMPAIGN_OFFER,
        customer: {
          name: data.name,
          email: data.email,
          phone: data.phone,
        },
        dealership: {
          email: data.dealership,
          label: selectedDealership?.label ?? "",
        },
        vehicle: {
          model: "Puma",
        },
        campaign: {
          slug,
        },
        source: {
          pageUrl: window.location.href,
        },
        title: "Запитване за оферта - Ford Puma 2026",
      });

      navigate("/thank-you-testdrive");
    } catch (error) {
      console.error("Грешка при запис в Firebase:", error);
    }
  };

  const renderContactForm = () => {
    const slug = "Puma 2025GetInTouch";

    return (
      <form
        onSubmit={handleSubmit((data) => marketingCampaign(data, slug))}
        className="mx-auto w-full max-w-3xl px-6 py-12 sm:px-8"
        noValidate
      >
        <h2 className="text-center text-xl tracking-wider uppercase text-fordBlue">
          Поискай оферта
        </h2>
        <FieldGroup className="mt-8 gap-5">
          <Field data-invalid={!!errors.name}>
            <FieldLabel htmlFor="name">Име *</FieldLabel>
            <Input
              id="name"
              aria-invalid={!!errors.name}
              className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
              {...register("name")}
            />
            <FieldError>{errors.name?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.email}>
            <FieldLabel htmlFor="email">Имейл *</FieldLabel>
            <Input
              id="email"
              type="email"
              aria-invalid={!!errors.email}
              className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
              {...register("email")}
            />
            <FieldError>{errors.email?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.phone}>
            <FieldLabel htmlFor="phone">Телефон *</FieldLabel>
            <Input
              id="phone"
              type="text"
              aria-invalid={!!errors.phone}
              className="h-11 rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
              {...register("phone")}
            />
            <FieldError>{errors.phone?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.dealership}>
            <FieldLabel>Предпочитано представителство *</FieldLabel>
            <Controller
              name="dealership"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    className="h-12 w-full rounded-xl border-slate-200 bg-[rgb(248,250,252)] px-4 text-base shadow-none focus-visible:border-fordBlue focus-visible:ring-0"
                    aria-invalid={!!errors.dealership}
                  >
                    <SelectValue placeholder="Моля, изберете" />
                  </SelectTrigger>
                  <SelectContent className="bg-white font-fordAntennaRegular">
                    {DEALERSHIP_OPTIONS.map((dealership) => (
                      <SelectItem
                        key={dealership.email}
                        value={dealership.email}
                      >
                        {dealership.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FieldError>{errors.dealership?.message}</FieldError>
          </Field>
        </FieldGroup>

        <div className="flex justify-center pt-8">
          <Button
            type="submit"
            size="lg"
            disabled={isSubmitting}
            className="min-h-11 rounded-full bg-fordBlue px-10 text-white hover:bg-fordLight"
          >
            {isSubmitting ? "Изпращане..." : "Изпрати"}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="font-fordAntennaRegular scroll-smooth">
      <div className="relative w-full">
        <picture>
          <source
            media="(max-width: 1024px)"
            srcSet="/images/puma2024/puma-2026-mobile.jpeg"
          />
          <img
            src="/images/puma2024/puma-2026-desktop.jpg"
            alt="Новият Ford Puma 2026"
            className="w-full"
          />
        </picture>
        {/* Заглавие */}
        <h1 className="absolute w-full text-center px-4 text-4xl md:text-6xl font-fordBlack text-white drop-shadow-md left-1/2 top-[55%] md:top-[5%] transform -translate-x-1/2 uppercase">
          Новият Ford Puma
        </h1>
        {/* <h1 className="absolute w-full text-center px-4 text-xl md:text-4xl font-fordMedium text-white drop-shadow-md left-1/2 top-[80%] md:top-[12%] transform -translate-x-1/2">
          Сега на специална цена от 41 990лв.
          <spaan className="text-sm align-super">*</spaan>
        </h1> */}
        {/* Подзаглавие */}
        <p className="absolute md:block text-xl md:text-3xl w-full text-center md:font-fordMedium text-white drop-shadow-md left-1/2 top-[73%] tracking-tight md:tracking-normal md:top-[20%] transform -translate-x-1/2">
          Вземи сега на СПЕЦИАЛНА<sup className="text-xs align-super">*</sup>{" "}
          цена <br className="md:hidden" />и с първоначален разход
          <sup className="text-xs align-super">**</sup>
          <br className="md:hidden" /> от
          <span className="font-fordBlack"> 1 990 € / 3 892 лв.</span>
        </p>
      </div>
      <p className="px-4 mt-16 text-xl text-left break-words md:text-center lg:px-72 text-fordBlue">
        Сега още по-сигурен и безопасен, още по-технологичен и още по-свързан!
        Компактен, динамичен и стилен, Ford Puma е създаден, за да се справя с
        всяко предизвикателство в градска среда. Изпитайте удоволствието от
        шофирането и усетете уникалното съчетание между спортен дух и модерен
        дизайн.
      </p>
      <div className="flex justify-center mt-8 text-center margin-auto">
        <button
          className="px-12 py-2 mx-8 mt-8 border rounded-full lg:mt-auto border-fordBlue text-fordBlue hover:bg-fordBlue hover:text-white"
          onClick={() => {
            const ofertaSection = document.getElementById("poiskayOferta");
            if (ofertaSection) {
              ofertaSection.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }}
        >
          Поискай оферта
        </button>
      </div>
      <div className="relative mt-16">
        <img
          src="/images/puma2024/2024_FORD_PUMA_25-LOW.jpg"
          alt=""
          className="w-full"
        />
        <ul className="w-full px-6 py-8 text-lg text-white list-outside bg-fordBlue lg:bg-transparent lg:bg-gradient-to-b lg:from-black/10 lg:via-black/20 lg:to-black/80 lg:absolute lg:bottom-0 lg:grid lg:grid-cols-2 lg:px-10 lg:gap-x-8">
          <li className="mt-4">
            <span className="font-bold">Мощен и икономичен</span> – с последно
            поколение мека хибридна технология за по-нисък разход на гориво.
          </li>
          <li className="mt-4">
            <span className="font-bold">Смарт функции</span> – оборудван с
            най-новите технологии за свързаност и в помощ на в водача,
            включително адаптивен круиз контрол и 360-градусова камера.
          </li>
          <li className="mt-4">
            <span className="font-bold">Сигурен и безопасен</span> – Оборудван с
            най-усъвършенстваните технологии за сигурлност и безопасност,
            включително Асистент за предотвратяване на сблъсък и aвтоматично
            задействане на спирачките след инцидент; Асистент за следене на
            пътната лента; система за следене умората у водача и показателите на
            шофиране.
          </li>
          <li className="mt-4">
            <span className="font-bold">Стилен и практичен</span> – с гъвкаво
            багажно пространство от 456 литра и просторен и стилен интериор,
            който отговаря на всеки стил.
          </li>
        </ul>
      </div>
      <section className="grid grid-cols-1 gap-4 px-4 mt-16 text-lg lg:grid-cols-4 text-fordBlue">
        <div className="p-4 rounded-md shadow-md">
          <h3 className="py-4 text-center text-white uppercase rounded-md bg-fordLight">
            Titanium
          </h3>
          <p className="p-4 leading-6 text-center text-fordBlue bg-gray-50">
            За тези, които предпочитат
            <br />
            по-комфортно шофиране
          </p>
          <ul className="pl-4 pr-2 mt-4 list-disc list-outside">
            <li>17" черни алуминиеви джанти</li>
            <li>LED предни фарове и LED дневни светлини, LED задни светлини</li>
            <li>
              12" сензорен екран с подобрена инфоразвлекателна система SYNC 4,
              Apple CarPlay/Android Auto, задни паркинг сензори.
            </li>
          </ul>
        </div>
        <div className="p-4 rounded-md shadow-md">
          <h3 className="py-4 text-center text-white uppercase rounded-md bg-fordLight">
            ST-LINE
          </h3>
          <p className="p-4 leading-6 text-center text-fordBlue bg-gray-50">
            За тези, които търсят спортна визия и по-енергично изживяване на
            пътя
          </p>
          <ul className="pl-4 pr-2 mt-4 list-disc list-inside">
            <li>17" алуминиеви джанти ST-Line дизайн</li>
            <li>Спортно окачване за по-динамично управление</li>
            <li>
              Уникален интериор с черна тапицерия на седалките с червени шевове
            </li>
          </ul>
        </div>
        <div className="p-4 rounded-md shadow-md">
          <h3 className="py-4 text-center text-white uppercase rounded-md bg-fordLight">
            ST-LINE X
          </h3>
          <p className="p-4 leading-6 text-center text-fordBlue bg-gray-50">
            Комбинира спортен стил с комфорт
            <br /> и първокласни технологии
          </p>
          <ul className="pl-4 pr-2 mt-4 list-disc list-inside">
            <li>18" алуминиеви джанти </li>
            <li>Тапицерия на седалките от кожа/велур с червени шевове</li>
            <li>Премиум аудиосистема B&O с 10 високоговорителя</li>
            <li>Електрическа врата на багажника</li>
          </ul>
        </div>
        <div className="p-4 rounded-md shadow-md">
          <h3 className="py-4 text-center text-white uppercase rounded-md bg-fordLight">
            ST
          </h3>
          <p className="py-4 leading-6 text-center text-fordBlue bg-gray-50">
            За търсещите адреналин на пътя
            <br /> и вълнуващо изживяване
          </p>
          <ul className="pl-4 pr-2 mt-4 list-disc list-inside">
            <li>19" алуминиеви джанти</li>
            <li>360° обзор над автомобила</li>
            <li>Адаптивен автопилот</li>
            <li>Отопляеми предни седалки, челно стъкло и волан</li>
          </ul>
        </div>
      </section>
      <h3 className="px-4 py-16 text-2xl text-center lg:px-64 text-fordLight">
        Puma 2024 се предлага с бензинови EcoBoost двигатели с мека хибридна
        технология с мощност 125 к.с., 155 к.с. и 160 к.с.
        <br />
        Може да избирате между 6-степенна механична или 7-степенна автоматична
        трансмисия.
      </h3>
      <div className="relative mt-4">
        <img
          src="/images/puma2024/2024_FORD_PUMA_6-LOW.jpg"
          alt=""
          className="w-full"
        />
        <div className="absolute bottom-0 w-full px-16 py-12 text-white bg-gradient-to-b from-transparent to-black/80">
          <p className="mt-2 text-2xl">С атрактивни условия за финансиране</p>
        </div>
      </div>
      <section className="grid my-16 text-lg lg:grid-cols-2">
        <h3 className="text-4xl text-center lg:text-right text-fordLight">
          Защо да изберете Ford Puma?
        </h3>
        <ul className="px-4 mt-8 lg:mt-0 lg:pr-48 lg:pl-16 text-fordBlue">
          <li>
            <span className="font-bold">Интелигентен дизайн и практичност</span>{" "}
            – мултифункционален и удобен автомобил, който пасва на всеки стил на
            живот.
          </li>
          <li className="mt-4">
            <span className="font-bold">Най-високо ниво на безопасност</span> –
            създаден с най-новите технологии за сигурност и безопасност, за да
            гарантира спокойствието ви на пътя.
          </li>
          <li className="mt-4">
            <span className="font-bold">Перфектен за града</span> – компактен,
            но просторен, с възможности за лесно маневриране и паркиране.
          </li>
        </ul>
      </section>
      <section className="flex flex-col items-center px-4 py-12 lg:justify-center lg:p-16 lg:flex-row bg-gray-50">
        <a href="https://sale.ford.bg/?model=Puma">
          <button className="px-12 py-2 mx-8 border rounded-full border-fordBlue text-fordBlue hover:bg-fordBlue hover:text-white">
            Виж налични автомобили
          </button>
        </a>
      </section>

      <motion.section
        id="poiskayOferta"
        className="w-full xl:w-[1300px] mx-auto py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 1.3 }}
      >
        {renderContactForm()}
      </motion.section>

      <section className="max-w-4xl mx-auto mt-4">
        <p className="px-4 text-sm text-fordBlue">
          * Специалната цена за Ford Puma Titanium се прилага при 100%
          финансиране и е изчислена на база стандартна цена 25 218,01 € / 49 330
          лв. с ДДС и преференциална крайна цена след отстъпка 22 191,85 € / 43
          402 лв. с ДДС.
          <br />
          ** Посочената първоначална вноска е за Ford Puma Titanium при лизингов
          договор със срок 60 месеца и включва регистрационни такси, застраховки
          и разходи по въвеждане в експлоатация. Офертата подлежи на одобрение
          от финансова институция. Валидна до 15.05.2026 г. или до изчерпване на
          количествата. <br />
          Изображенията са илюстративни.
        </p>
      </section>
      {/* {showModal && zapitvane && (
        <Modal handleClose={handleClose}>
          <GetInTouch model={model} copy={copy} />
        </Modal>
      )} */}
    </div>
  );
}

export default Puma2024;
